import {
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

const SERVICE_WORKER_PATH = '/service-worker.js';
const SUBSCRIPTIONS_COLLECTION = 'webPushSubscriptions';
const PUBLIC_VAPID_KEY = process.env.REACT_APP_WEB_PUSH_PUBLIC_VAPID_KEY;
const WEB_PUSH_SUBSCRIPTION_RETENTION_DAYS = 90;

const textEncoder = new TextEncoder();

const arrayBufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const getEndpointHash = async (endpoint) => {
  const digest = await crypto.subtle.digest('SHA-256', textEncoder.encode(endpoint));
  return arrayBufferToBase64Url(digest);
};

const getPlatform = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const userAgent = navigator.userAgent || '';

  if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  if (/Macintosh|Mac OS X/.test(userAgent)) return 'macos';
  if (/Windows/.test(userAgent)) return 'windows';

  return 'desktop';
};

const getDeviceLabel = () => {
  if (typeof navigator === 'undefined') return 'Unbekanntes Gerät';
  const platform = getPlatform();
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone === true;
  const suffix = standalone ? ' PWA' : ' Browser';

  switch (platform) {
    case 'ios':
      return `iOS${suffix}`;
    case 'android':
      return `Android${suffix}`;
    case 'macos':
      return `macOS${suffix}`;
    case 'windows':
      return `Windows${suffix}`;
    default:
      return `Web${suffix}`;
  }
};

const subscriptionToJson = (subscription) => {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    keys: json.keys || {}
  };
};

export const isWebPushSupported = () => (
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window &&
  !!window.crypto?.subtle
);

export const isWebPushConfigured = () => Boolean(PUBLIC_VAPID_KEY);

export const getWebPushStatus = async () => {
  const supported = isWebPushSupported();
  const configured = isWebPushConfigured();
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  if (!supported || !configured) {
    return {
      supported,
      configured,
      permission,
      active: false,
      subscriptionId: null
    };
  }

  const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
    || await navigator.serviceWorker.getRegistration('/');
  const subscription = registration ? await registration.pushManager.getSubscription() : null;

  if (!subscription) {
    return {
      supported,
      configured,
      permission,
      active: false,
      subscriptionId: null
    };
  }

  const subscriptionId = await getEndpointHash(subscription.endpoint);

  return {
    supported,
    configured,
    permission,
    active: true,
    subscriptionId
  };
};

export const enableWebPushForCurrentDevice = async (user) => {
  if (!user?.uid) {
    throw new Error('Kein angemeldeter Benutzer gefunden.');
  }

  if (!isWebPushSupported()) {
    throw new Error('Web Push wird von diesem Browser nicht unterstützt.');
  }

  if (!isWebPushConfigured()) {
    throw new Error('Der öffentliche Web-Push-Schlüssel ist nicht konfiguriert.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Benachrichtigungen wurden nicht erlaubt.');
  }

  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
  });

  const subscriptionId = await getEndpointHash(subscription.endpoint);
  const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
  const existingDoc = await getDoc(subscriptionRef);
  const subscriptionData = subscriptionToJson(subscription);

  await setDoc(subscriptionRef, {
    ...subscriptionData,
    userId: user.uid,
    role: user.role || null,
    operatorId: user.operatorId || null,
    enabled: true,
    platform: getPlatform(),
    deviceLabel: getDeviceLabel(),
    userAgent: navigator.userAgent || '',
    updatedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    failureCount: 0,
    lastError: null,
    ...(existingDoc.exists()
      ? { expiresAt: deleteField() }
      : { createdAt: serverTimestamp() })
  }, { merge: true });

  return {
    subscriptionId,
    permission,
    active: true
  };
};

export const disableWebPushForCurrentDevice = async () => {
  if (!isWebPushSupported()) {
    return { active: false };
  }

  const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
    || await navigator.serviceWorker.getRegistration('/');
  const subscription = registration ? await registration.pushManager.getSubscription() : null;

  if (!subscription) {
    return { active: false };
  }

  const subscriptionId = await getEndpointHash(subscription.endpoint);
  await subscription.unsubscribe();

  try {
    await updateDoc(doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId), {
      enabled: false,
      disabledAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + WEB_PUSH_SUBSCRIPTION_RETENTION_DAYS * 24 * 60 * 60 * 1000),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.warn('Web-Push-Subscription konnte in Firestore nicht deaktiviert werden:', error);
  }

  return {
    subscriptionId,
    active: false
  };
};
